import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Copy, KeyRound, Loader2, UserPlus, Pencil, Ban, CheckCircle2, X, Check,
} from "lucide-react";
import { formatApiError, useAuth, ROLE_DEFAULT_MODULES } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full p-2.5 rounded-xl bg-soft border border-transparent focus:border-mint focus:outline-none text-sm text-forest";
const labelCls = "block text-xs font-bold uppercase tracking-[0.12em] text-mid-neutral mb-1";
const ROLE_LABELS = {
  admin: "Admin — full access incl. team management",
  manager: "Manager — leads, clients, services",
  sales: "Sales — leads and clients",
  viewer: "Viewer — read-only",
};

export default function UsersList() {
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [invite, setInvite] = useState({ name: "", email: "", role: "sales", moduleAccess: [...ROLE_DEFAULT_MODULES.sales] });
  const [inviting, setInviting] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [inviteMsg, setInviteMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const isAdmin = me?.role === "admin";

  const load = useCallback(async () => {
    try {
      setError("");
      const r = await axios.get(`${API}/admin/users`);
      setData(r.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleModule = (state, setState, mod) => {
    const has = state.moduleAccess.includes(mod);
    setState({
      ...state,
      moduleAccess: has ? state.moduleAccess.filter((m) => m !== mod) : [...state.moduleAccess, mod],
    });
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setError("");
    setTempPassword(null);
    setInviteMsg("");
    try {
      const r = await axios.post(`${API}/admin/users`, invite);
      if (r.data.inviteSent) {
        setInviteMsg(`Invite email sent to ${r.data.user.email} — they'll set their own password (link valid 48 hours).`);
      } else if (r.data.tempPassword) {
        setTempPassword({ email: r.data.user.email, password: r.data.tempPassword });
      }
      setInvite({ name: "", email: "", role: "sales", moduleAccess: [...ROLE_DEFAULT_MODULES.sales] });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setInviting(false);
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setDraft({ name: u.name, role: u.role, moduleAccess: [...(u.moduleAccess || [])] });
  };

  const saveEdit = async (u) => {
    setSavingId(u.id);
    setError("");
    try {
      await axios.patch(`${API}/admin/users/${u.id}`, draft);
      setEditingId(null);
      setDraft(null);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSavingId(null);
    }
  };

  const toggleActive = async (u) => {
    setSavingId(u.id);
    setError("");
    try {
      await axios.patch(`${API}/admin/users/${u.id}`, { active: !(u.active !== false) });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSavingId(null);
    }
  };

  const resetPassword = async (u) => {
    setSavingId(u.id);
    setError("");
    setTempPassword(null);
    setInviteMsg("");
    try {
      const r = await axios.patch(`${API}/admin/users/${u.id}`, { resetPassword: true });
      if (r.data.inviteSent) {
        setInviteMsg(`Password reset email sent to ${u.email} — they'll set a new password (link valid 48 hours).`);
      } else {
        setTempPassword({ email: u.email, password: r.data.tempPassword });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSavingId(null);
    }
  };

  const copyTemp = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  if (!data) {
    if (error) {
      return (
        <div data-testid="users-error" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-40 text-mid-neutral">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading team…
      </div>
    );
  }

  return (
    <div data-testid="admin-users" className="space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-mint">CRM</p>
        <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-forest">Team & roles</h1>
        <p className="mt-2 text-mid-neutral">
          Invite team members and control which CRM modules each person can access.
        </p>
      </header>

      {error && (
        <div data-testid="users-error" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {inviteMsg && (
        <div data-testid="invite-sent-box" className="rounded-2xl border-2 border-lime bg-white p-5">
          <p className="text-sm font-bold text-forest flex items-center gap-2">
            <Check className="h-4 w-4 text-mint" /> {inviteMsg}
          </p>
          <p className="mt-1 text-xs text-mid-neutral">
            If it doesn't arrive in a few minutes, ask them to check spam — DKIM for greencirclegroup.com.au is still pending.
          </p>
        </div>
      )}

      {tempPassword && (
        <div data-testid="temp-password-box" className="rounded-2xl border-2 border-lime bg-white p-5">
          <p className="text-sm font-bold text-forest">
            Temporary password for {tempPassword.email}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code data-testid="temp-password-value" className="rounded-lg bg-soft px-3 py-2 text-sm font-mono font-bold text-forest">
              {tempPassword.password}
            </code>
            <button
              type="button"
              onClick={copyTemp}
              data-testid="temp-password-copy"
              className="inline-flex items-center gap-1.5 rounded-lg bg-forest text-white px-3 py-2 text-xs font-bold"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
            <button
              type="button"
              onClick={() => setTempPassword(null)}
              data-testid="temp-password-dismiss"
              className="inline-flex items-center gap-1.5 rounded-lg bg-soft text-forest px-3 py-2 text-xs font-bold"
            >
              <X className="h-3.5 w-3.5" /> Dismiss
            </button>
          </div>
          <p className="mt-2 text-xs text-mid-neutral">
            Share this securely — it is shown only once. The user will be asked to set a new password on first login.
          </p>
        </div>
      )}

      {isAdmin && (
        <section className="rounded-2xl bg-white border border-border-neutral p-6">
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
            <UserPlus className="h-4 w-4 text-mint" /> Invite a team member
          </h2>
          <form onSubmit={submitInvite} className="mt-4 space-y-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Name</label>
                <input
                  required
                  value={invite.name}
                  onChange={(e) => setInvite({ ...invite, name: e.target.value })}
                  data-testid="invite-name"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input
                  required
                  type="email"
                  value={invite.email}
                  onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                  data-testid="invite-email"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Role</label>
                <select
                  value={invite.role}
                  onChange={(e) => setInvite({ role: e.target.value, name: invite.name, email: invite.email, moduleAccess: [...(ROLE_DEFAULT_MODULES[e.target.role] || [])] })}
                  data-testid="invite-role"
                  className={inputCls}
                >
                  {data.roles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <p className="text-xs text-mid-neutral -mt-2">{ROLE_LABELS[invite.role]}</p>
            <div>
              <label className={labelCls}>Module access</label>
              <ModuleChecks
                modules={data.modules}
                selected={invite.moduleAccess}
                onToggle={(m) => toggleModule(invite, setInvite, m)}
                prefix="invite"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={inviting || !invite.name.trim() || !invite.email.trim()}
                data-testid="invite-submit"
                className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2.5 text-sm font-bold disabled:opacity-50"
              >
                {inviting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
                Create user
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="rounded-2xl bg-white border border-border-neutral overflow-hidden">
        {data.users.map((u) => {
          const active = u.active !== false;
          const editing = editingId === u.id;
          return (
            <div key={u.id} data-testid={`user-row-${u.email}`} className="p-4 border-b border-border-neutral last:border-0">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-forest truncate">
                    {u.name} <span className="text-mid-neutral font-normal">· {u.email}</span>
                  </p>
                  <p className="text-xs text-mid-neutral">
                    <span className="uppercase tracking-widest font-bold text-[10px] text-forest">{u.role}</span>
                    {" · "}{(u.moduleAccess || []).join(", ") || "no modules"}
                    {u.lastLoginAt ? ` · last login ${formatTs(u.lastLoginAt)}` : " · never logged in"}
                  </p>
                </div>
                <span className={"inline-block rounded-full px-3 py-1 text-xs font-bold " + (active ? "bg-lime text-forest" : "bg-red-100 text-red-800")}>
                  {active ? "Active" : "Deactivated"}
                </span>
                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => (editing ? setEditingId(null) : startEdit(u))}
                      data-testid={`user-edit-${u.email}`}
                      className="inline-flex items-center gap-1 rounded-lg bg-soft text-forest px-2.5 py-1.5 text-xs font-bold"
                    >
                      <Pencil className="h-3 w-3" /> {editing ? "Cancel" : "Edit"}
                    </button>
                    <button
                      type="button"
                      onClick={() => resetPassword(u)}
                      disabled={savingId === u.id}
                      data-testid={`user-reset-${u.email}`}
                      className="inline-flex items-center gap-1 rounded-lg bg-soft text-forest px-2.5 py-1.5 text-xs font-bold disabled:opacity-50"
                    >
                      <KeyRound className="h-3 w-3" /> Reset password
                    </button>
                    {u.id !== me?.id && (
                      <button
                        type="button"
                        onClick={() => toggleActive(u)}
                        disabled={savingId === u.id}
                        data-testid={`user-toggle-active-${u.email}`}
                        className={"inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-bold disabled:opacity-50 " + (active ? "bg-red-50 text-red-700" : "bg-lime text-forest")}
                      >
                        {active ? <Ban className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                        {active ? "Deactivate" : "Reactivate"}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {editing && draft && (
                <div className="mt-4 rounded-xl bg-soft/50 p-4 space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Name</label>
                      <input
                        value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        data-testid={`user-edit-name-${u.email}`}
                        className={inputCls + " bg-white"}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Role</label>
                      <select
                        value={draft.role}
                        onChange={(e) => setDraft({ name: draft.name, role: e.target.value, moduleAccess: [...(ROLE_DEFAULT_MODULES[e.target.value] || [])] })}
                        data-testid={`user-edit-role-${u.email}`}
                        className={inputCls + " bg-white"}
                      >
                        {data.roles.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Module access</label>
                    <ModuleChecks
                      modules={data.modules}
                      selected={draft.moduleAccess}
                      onToggle={(m) => toggleModule(draft, setDraft, m)}
                      prefix={`edit-${u.email}`}
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => saveEdit(u)}
                      disabled={savingId === u.id}
                      data-testid={`user-edit-save-${u.email}`}
                      className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-3 py-1.5 text-xs font-bold disabled:opacity-50"
                    >
                      {savingId === u.id && <Loader2 className="h-3 w-3 animate-spin" />} Save changes
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
}

function ModuleChecks({ modules, selected, onToggle, prefix }) {
  return (
    <div className="flex flex-wrap gap-4">
      {modules.map((m) => (
        <label key={m} className="inline-flex items-center gap-1.5 text-xs font-semibold text-forest">
          <input
            type="checkbox"
            checked={selected.includes(m)}
            onChange={() => onToggle(m)}
            data-testid={`${prefix}-module-${m}`}
            className="h-3.5 w-3.5 accent-forest"
          />
          {m}
        </label>
      ))}
    </div>
  );
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}
